import React, {
    Fragment,
    useState,
    useEffect,
    useCallback,
    useMemo,
} from 'react';
import { observer } from 'mobx-react';
import {
    Link,
    Route,
    Routes,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';
import { DropdownMenu } from 'reactstrap';
import Modal from 'react-modal';
import { Menu, MenuItem } from '@mui/material';
import { trainingName } from '../../../utils/helpers';
import {
    Block,
    palette,
    NestedMenuItem,
    DarkArrowTooltipIcon,
} from '@awarego-ui/components';
import {
    ThemedDropdownItem,
    ThemedNav,
    ThemedNavItem,
    ThemedNavLink,
} from '../../../components/themed/ThemedComponents';
import { useStores } from '../../../hooks/use-stores';
import ThemedSpinner from '../../../components/themed/ThemedSpinner';
import ErrorMessage from '../../../components/errormessage';
import RequireRole from '../../RequireRole';
import HeaderSection from '../../../components/header/HeaderSection';
import SubHeaderComponentsList from '../../../components/header/SubHeaderComponentsList';
import ScormSubHeading from './controls/ScormSubHeading';
import ScormOverview from './overview/ScormOverview';
import ScormScenarios from './scenarios/ScormScenarios';
import ScormTrainingEmployees from './employees/ScormTrainingEmployees';
import TrainingThreatAreas from './threatAreas/TrainingThreatAreas';
import ModalRename from '../../ModalRename';

function ScormAutomationView({ companyId }) {
    const { id } = useParams();
    const automationId = id; // already 'p1044' form

    const {
        automationStore,
        store,
        commonStore,
        cmi5Store,
    } = useStores();

    const {
        editingAutomation,
        loadingAutomation,
        latestTenAutomations,
        chartMetrics,
        chartMetricsLoading,
        chartMetricsMigrating,
        error,
    } = automationStore;

    const [renameModal, setRenameModal] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    const chartsEnabled =
        store.currentCompany &&
        store.currentCompanyUISettings['ui.trainings.charts'];

    useEffect(() => {
        const doLoad = async () => {
            const failed = await automationStore.loadAutomation(
                companyId,
                automationId
            );
            if (chartsEnabled)
                void automationStore.loadAutomationChartMetrics(
                    companyId,
                    automationId
                );
            await automationStore.loadLatestTenAutomations(companyId);
            if (failed) {
                automationStore.error = null;
                navigate('/trainings');
            }
        };
        doLoad();
    }, [companyId, automationId]);

    useEffect(() => {
        return () => {
            automationStore.perUserDetailsLoaded = false;
        };
    }, []);

    const score = editingAutomation
        ? editingAutomation.awarenessScore
        : 0;

    const onDelete = useCallback(() => {
        commonStore.showConfirm(
            'Are you sure you want to delete this training?',
            'Delete',
            'Delete Training',
            async () => {
                if (await automationStore.delete(companyId, automationId))
                    navigate('/trainings');
            },
            true
        );
    }, [automationStore, commonStore, companyId, automationId, navigate]);

    const handleDownloadPackage = useCallback(
        (type) => {
            if (!editingAutomation) return;
            cmi5Store.downloadZIP(
                companyId,
                editingAutomation.id,
                editingAutomation._rawData.uniqueId,
                type
            );
        },
        [cmi5Store, companyId, editingAutomation]
    );

    const latestTenAutomationsCount = () =>
        latestTenAutomations.filter(
            (a) => a.id !== editingAutomation?.id
        ).length;

    const tab = useMemo(() => {
        const marker = `/trainings/${automationId}/view`;
        const relative = location.pathname.split(marker)[1] || '';
        return relative.replace(/^\//, '').split('/')[0] || '';
    }, [automationId, location.pathname]);

    const tabs = useMemo(() => {
        if (!editingAutomation) return [];
        return [
            {
                code: '',
                title: 'Overview',
                content: (
                    <ScormOverview
                        editingAutomation={editingAutomation}
                        chartMetrics={chartMetrics}
                        chartMetricsLoading={chartMetricsLoading}
                        chartMetricsMigrating={chartMetricsMigrating}
                        score={score}
                    />
                ),
            },
            {
                code: 'threatAreas',
                title: 'Threat areas',
                content: (
                    <TrainingThreatAreas
                        companyId={companyId}
                        training={{ ...editingAutomation, id: automationId }}
                    />
                ),
            },
            {
                code: 'scenarios',
                title: 'Scenarios',
                content: <ScormScenarios training={editingAutomation} />,
            },
            {
                code: 'employees',
                title: 'Employees',
                content: (
                    <ScormTrainingEmployees
                        companyId={companyId}
                        training={editingAutomation}
                    />
                ),
            },
        ];
    }, [
        chartMetrics,
        chartMetricsLoading,
        chartMetricsMigrating,
        companyId,
        editingAutomation,
        score,
    ]);

    const handleMenuOpen = (e) => setMenuAnchorEl(e.currentTarget);
    const handleMenuClose = () => setMenuAnchorEl(null);

    // Mirror ScormAutomationListTable: close the menu once a download finishes.
    useEffect(() => {
        if (cmi5Store.isZIPDownloading === false) setMenuAnchorEl(null);
    }, [cmi5Store.isZIPDownloading]);

    const createHeadingDropdown = useMemo(() => {
        if (!editingAutomation) return;
        return (
            <DropdownMenu>
                {automationStore.loadingLatestTenAutomations ? (
                    <ThemedSpinner />
                ) : (
                    latestTenAutomations.map((x, i) => {
                        if (x.id !== editingAutomation.id)
                            return (
                                <ThemedDropdownItem key={`${i}-${x.id}`}>
                                    <Link to={x.link}>{x.name}</Link>
                                </ThemedDropdownItem>
                            );
                    })
                )}
            </DropdownMenu>
        );
    }, [
        editingAutomation,
        latestTenAutomations,
        automationStore.loadingLatestTenAutomations,
    ]);

    return (
        <Fragment>
            <Link to="/trainings/">
                <div className="navigation back-button">
                    Back to training overview
                </div>
            </Link>
            <ErrorMessage error={error} />
            {loadingAutomation ? (
                <ThemedSpinner />
            ) : (
                <Fragment>
                    {editingAutomation ? (
                        <Fragment>
                            <HeaderSection
                                heading={trainingName(
                                    editingAutomation.name,
                                    editingAutomation._rawData.created
                                )}
                                headingDropdown={createHeadingDropdown}
                                headingDropdownContentCount={latestTenAutomationsCount()}
                                subHeadings={
                                    <SubHeaderComponentsList>
                                        <ScormSubHeading
                                            editingAutomation={
                                                editingAutomation
                                            }
                                        />
                                    </SubHeaderComponentsList>
                                }
                                unguardedButtons={
                                    <DarkArrowTooltipIcon
                                        row={editingAutomation}
                                        customFunction={(e) =>
                                            handleMenuOpen(e)
                                        }
                                        title="More"
                                        icon="more"
                                    />
                                }
                            />
                            <Menu
                                anchorEl={menuAnchorEl}
                                keepMounted
                                open={Boolean(menuAnchorEl)}
                                onClose={handleMenuClose}
                                elevation={1}
                            >
                                <NestedMenuItem
                                    text="Download training"
                                    loading={cmi5Store.isZIPDownloading}
                                    items={[
                                        {
                                            text: 'SCORM 1.2',
                                            props: {
                                                onClick: () =>
                                                    handleDownloadPackage(
                                                        'scorm12'
                                                    ),
                                            },
                                        },
                                        {
                                            text: 'SCORM 2004 3rd Edition',
                                            props: {
                                                onClick: () =>
                                                    handleDownloadPackage(
                                                        'scorm2004'
                                                    ),
                                            },
                                        },
                                        {
                                            text: 'cmi5',
                                            props: {
                                                onClick: () =>
                                                    handleDownloadPackage(
                                                        'cmi5'
                                                    ),
                                            },
                                        },
                                    ]}
                                />
                                <RequireRole>
                                    {editingAutomation.canRename && (
                                        <MenuItem
                                            onClick={() => {
                                                setRenameModal(true);
                                                handleMenuClose();
                                            }}
                                        >
                                            Rename training
                                        </MenuItem>
                                    )}
                                    {editingAutomation.canDelete && (
                                        <MenuItem
                                            onClick={() => {
                                                onDelete();
                                                handleMenuClose();
                                            }}
                                        >
                                            <span
                                                style={{
                                                    color: palette.vibrant.red,
                                                }}
                                            >
                                                Delete training
                                            </span>
                                        </MenuItem>
                                    )}
                                </RequireRole>
                            </Menu>
                            <Block resetPadding py={0} px={24}>
                                <ThemedNav>
                                    {tabs.map((t, i) => (
                                        <ThemedNavItem
                                            active={tab === t.code}
                                            key={t.code + '_' + i}
                                            strongerMargin
                                        >
                                            <ThemedNavLink
                                                to={t.code}
                                                as={Link}
                                                $mildPadding
                                            >
                                                {t.title}
                                            </ThemedNavLink>
                                        </ThemedNavItem>
                                    ))}
                                </ThemedNav>
                            </Block>
                            <Routes>
                                <Route path="/" element={tabs[0].content} />
                                {tabs
                                    .slice()
                                    .reverse()
                                    .map((t) => (
                                        <Route
                                            key={t.code}
                                            path={t.code}
                                            element={t.content}
                                        />
                                    ))}
                            </Routes>
                            <Modal
                                isOpen={renameModal}
                                onRequestClose={() => setRenameModal(false)}
                                className="Modal"
                            >
                                <ModalRename
                                    onRequestClose={() =>
                                        setRenameModal(false)
                                    }
                                    companyId={companyId}
                                    item={editingAutomation}
                                    itemStore={automationStore}
                                />
                            </Modal>
                        </Fragment>
                    ) : (
                        !error && <ThemedSpinner />
                    )}
                </Fragment>
            )}
        </Fragment>
    );
}

export default observer(ScormAutomationView);
